import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { auth } from '../config/firebase';
// Note: Location/category calculation moved to backend - frontend preserves existing values

const ListsContext = createContext();

// Session storage keys for lists cache
const LISTS_CACHE_KEY = 'vibary_lists_cache';
const COLLAB_CACHE_KEY = 'vibary_collab_lists_cache';
const ARCHIVED_CACHE_KEY = 'vibary_archived_lists_cache';

// Get cached lists from sessionStorage
const getCachedLists = (key) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Ignore errors
  }
  return null;
};

// Save lists to sessionStorage cache
const setCachedLists = (key, data) => {
  try {
    if (data && data.length > 0) {
      // Limit cache size to prevent quota issues - only cache essential fields
      const lightData = data.map(list => ({
        id: list.id,
        title: list.title,
        name: list.name,
        category: list.category,
        location: list.location,
        locationOverride: list.locationOverride,
        categoryOverride: list.categoryOverride,
        isPublic: list.isPublic,
        isOwned: list.isOwned,
        userId: list.userId,
        createdAt: list.createdAt,
        updatedAt: list.updatedAt,
        customPhoto: list.customPhoto,
        // Store only count and basic info, not full items array
        // Only cache items that have names (complete data) to avoid showing blank cards on refresh
        itemCount: (list.items || []).length,
        items: (list.items || []).slice(0, 3).filter(item => item.name).map(item => ({
          id: item.id,
          placeId: item.placeId,
          name: item.name,
          address: item.address,
          photoUrl: item.photoUrl,
        })),
        // Cache placeNames array for search functionality (lightweight string array)
        placeNames: list.placeNames || [],
        collaborators: list.collaborators,
        pinnedAt: list.pinnedAt,
        rank: list.rank,
      }));
      sessionStorage.setItem(key, JSON.stringify(lightData));
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (error) {
    // Storage quota exceeded - clear old data and don't cache
    if (error.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(key);
        // Also clear old discover cache if it exists
        sessionStorage.removeItem('discoverData');
      } catch {
        // Ignore cleanup errors
      }
    }
  }
};

// Clear all lists cache (used on logout or user change)
const clearListsCache = () => {
  try {
    sessionStorage.removeItem(LISTS_CACHE_KEY);
    sessionStorage.removeItem(COLLAB_CACHE_KEY);
    sessionStorage.removeItem(ARCHIVED_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

export function useLists() {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error('useLists must be used within a ListsProvider');
  }
  return context;
}

export function ListsProvider({ children }) {
  // Initialize from cache for instant render on page refresh
  const [lists, setLists] = useState(() => getCachedLists(LISTS_CACHE_KEY) || []);
  const [collaborationLists, setCollaborationLists] = useState(() => getCachedLists(COLLAB_CACHE_KEY) || []);
  const [archivedLists, setArchivedLists] = useState(() => getCachedLists(ARCHIVED_CACHE_KEY) || []);
  // Track if archived lists have been loaded (lazy-loaded on demand)
  const [archivedLoaded, setArchivedLoaded] = useState(!!getCachedLists(ARCHIVED_CACHE_KEY));
  const [loadingArchived, setLoadingArchived] = useState(false);
  // Only show loading if no cached data exists
  const cachedLists = getCachedLists(LISTS_CACHE_KEY);
  const [loading, setLoading] = useState(!cachedLists);
  const { user } = useAuth();
  const lastUserIdRef = useRef(null);
  const [firebaseReady, setFirebaseReady] = useState(!!auth.currentUser);

  // Listen for Firebase auth state to know when we can make authenticated requests
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseReady(!!firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch lists when user changes AND Firebase auth is ready
  useEffect(() => {
    if (user && (user.userId || user.id) && firebaseReady) {
      const userId = user.userId || user.id;

      // If user changed, clear old cache
      if (lastUserIdRef.current && lastUserIdRef.current !== userId) {
        clearListsCache();
        setLists([]);
        setCollaborationLists([]);
        setArchivedLists([]);
        setArchivedLoaded(false);
      }
      lastUserIdRef.current = userId;

      // Parallelize all fetches for faster initial load (stale-while-revalidate)
      Promise.all([
        fetchListsInternal(userId),
        fetchCollaborationListsInternal(),
        fetchArchivedListsInternal()
      ]);
    } else if (!user) {
      clearListsCache();
      setLists([]);
      setCollaborationLists([]);
      setArchivedLists([]);
      setArchivedLoaded(false);
      lastUserIdRef.current = null;
    }
  }, [user, firebaseReady]);

  // Internal fetch functions used by the parallel useEffect
  const fetchListsInternal = async (userId) => {
    if (!userId) {
      console.error('Failed to fetch lists: No user ID available');
      return;
    }
    // Wait for Firebase auth to be ready before making authenticated requests
    if (!auth.currentUser) {
      return;
    }
    try {
      setLoading(true);
      // Fetch all lists at once (limit 500 matches backend MAX_LISTS_FETCH)
      const response = await api.getLists(userId, { limit: 500 });
      const data = response.data || [];
      setLists(data);
      setCachedLists(LISTS_CACHE_KEY, data);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
      // Only clear if no cached data (preserve stale data on network error)
      if (!getCachedLists(LISTS_CACHE_KEY)) {
        setLists([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCollaborationListsInternal = async () => {
    // Wait for Firebase auth to be ready before making authenticated requests
    if (!auth.currentUser) {
      return;
    }
    try {
      const response = await api.getCollaborationLists();
      const data = response.data || [];
      setCollaborationLists(data);
      setCachedLists(COLLAB_CACHE_KEY, data);
    } catch (error) {
      console.error('Failed to fetch collaboration lists:', error);
      if (!getCachedLists(COLLAB_CACHE_KEY)) {
        setCollaborationLists([]);
      }
    }
  };

  const fetchArchivedListsInternal = async (force = false) => {
    // Wait for Firebase auth to be ready before making authenticated requests
    if (!auth.currentUser) {
      return;
    }
    // Don't refetch if already loaded (unless forced)
    if (archivedLoaded && !loadingArchived && !force) {
      return;
    }
    setLoadingArchived(true);
    try {
      const response = await api.getArchivedLists();
      const data = response.data || [];
      setArchivedLists(data);
      setCachedLists(ARCHIVED_CACHE_KEY, data);
      setArchivedLoaded(true);
    } catch (error) {
      console.error('Failed to fetch archived lists:', error);
      if (!getCachedLists(ARCHIVED_CACHE_KEY)) {
        setArchivedLists([]);
      }
    } finally {
      setLoadingArchived(false);
    }
  };

  // Public fetch functions that can be called from components
  const fetchLists = async (userIdParam) => {
    const userId = userIdParam || user?.userId || user?.id;
    return fetchListsInternal(userId);
  };

  const fetchCollaborationLists = async () => {
    return fetchCollaborationListsInternal();
  };

  // Fetch archived lists (can be forced to refresh)
  const fetchArchivedLists = async (force = false) => {
    return fetchArchivedListsInternal(force);
  };

  const addPlaceToList = async (place, listId) => {
    try {
      // Get the current list (convert to string for consistent comparison)
      // Check both owned lists and collaboration lists
      const currentList = lists.find((list) => String(list.id) === String(listId)) ||
                          collaborationLists.find((list) => String(list.id) === String(listId));
      if (!currentList) {
        throw new Error('List not found');
      }
      const isCollabList = collaborationLists.some((list) => String(list.id) === String(listId));

      // Use the optimized single-place endpoint
      const newPlace = { ...place, id: place.id || Date.now() };
      const response = await api.addPlaceToListOptimized(listId, newPlace);

      // Update local state with the backend-calculated location/category
      const currentItems = currentList.items || [];
      const updatedItems = [...currentItems, newPlace];

      const updateListFn = (list) => {
        if (String(list.id) === String(listId)) {
          return {
            ...list,
            items: updatedItems,
            // Use the location/category calculated by the backend
            location: response.location || list.location,
            category: response.category || list.category,
          };
        }
        return list;
      };

      // Update the correct state based on whether it's a collaboration list
      if (isCollabList) {
        setCollaborationLists((prevLists) => prevLists.map(updateListFn));
      } else {
        setLists((prevLists) => prevLists.map(updateListFn));
      }
    } catch (error) {
      console.error('Failed to add place to list:', error);
      throw error;
    }
  };

  // Batch add place to multiple lists using optimized batch endpoint
  const addPlaceToMultipleLists = async (place, listIds) => {
    if (!listIds || listIds.length === 0) return;

    try {
      // Use the optimized batch endpoint (single HTTP request + single Firestore batch write)
      const response = await api.batchAddPlaceToLists(place, listIds);

      // Build a map of listId -> {location, category} from the response
      const updatesMap = {};
      if (response.data?.updated) {
        response.data.updated.forEach(item => {
          if (item.listId) {
            updatesMap[item.listId] = { location: item.location, category: item.category };
          }
        });
      }

      // Update local state with backend-calculated location/category
      // Separate listIds into owned lists and collaboration lists
      const ownedListIds = listIds.filter(id => lists.some(l => String(l.id) === String(id)));
      const collabListIds = listIds.filter(id => collaborationLists.some(l => String(l.id) === String(id)));

      const updateListFn = (list) => {
        if (listIds.some(id => String(id) === String(list.id))) {
          const currentItems = list.items || [];
          const newPlace = { ...place, id: Date.now() + Math.random() };
          const updatedItems = [...currentItems, newPlace];
          const updates = updatesMap[list.id] || {};
          return {
            ...list,
            items: updatedItems,
            // Use the location/category calculated by the backend
            location: updates.location || list.location,
            category: updates.category || list.category,
          };
        }
        return list;
      };

      if (ownedListIds.length > 0) {
        setLists((prevLists) => prevLists.map(updateListFn));
      }
      if (collabListIds.length > 0) {
        setCollaborationLists((prevLists) => prevLists.map(updateListFn));
      }
    } catch (error) {
      console.error('Failed to add place to multiple lists:', error);
      throw error;
    }
  };

  // Batch delete place from multiple lists using optimized batch endpoint
  const deletePlaceFromMultipleLists = async (placeId, listIds) => {
    if (!listIds || listIds.length === 0) return;

    try {
      // Use the optimized batch endpoint (single HTTP request + single Firestore batch write)
      const response = await api.batchRemovePlaceFromLists(placeId, listIds);

      // Build a map of listId -> {location, category} from the response
      // Use String() for consistent key type
      const updatesMap = {};
      if (response.data?.updated) {
        response.data.updated.forEach(item => {
          if (item.listId) {
            updatesMap[String(item.listId)] = { location: item.location, category: item.category };
          }
        });
      }

      // Update local state with backend-calculated location/category
      // Separate listIds into owned lists and collaboration lists
      const ownedListIds = listIds.filter(id => lists.some(l => String(l.id) === String(id)));
      const collabListIds = listIds.filter(id => collaborationLists.some(l => String(l.id) === String(id)));

      const updateListFn = (list) => {
        if (listIds.some(id => String(id) === String(list.id))) {
          const currentItems = list.items || [];
          const updatedItems = currentItems.filter((item) => item.placeId !== placeId);
          // Use String() for consistent key lookup
          const updates = updatesMap[String(list.id)];
          return {
            ...list,
            items: updatedItems,
            // Use the location/category calculated by the backend
            // If backend returns null (empty list), clear the value instead of keeping stale data
            location: updates?.location !== undefined ? updates.location : list.location,
            category: updates?.category !== undefined ? updates.category : list.category,
          };
        }
        return list;
      };

      if (ownedListIds.length > 0) {
        setLists((prevLists) => prevLists.map(updateListFn));
      }
      if (collabListIds.length > 0) {
        setCollaborationLists((prevLists) => prevLists.map(updateListFn));
      }
    } catch (error) {
      console.error('Failed to delete place from multiple lists:', error);
      throw error;
    }
  };

  const updatePlaceInList = async (listId, placeId, updatedPlace) => {
    try {
      // Get the current list (convert to string for consistent comparison)
      // Check both owned lists and collaboration lists
      const currentList = lists.find((list) => String(list.id) === String(listId)) ||
                          collaborationLists.find((list) => String(list.id) === String(listId));
      if (!currentList) {
        throw new Error('List not found');
      }
      const isCollabList = collaborationLists.some((list) => String(list.id) === String(listId));

      // Update in Firebase using the efficient single-place endpoint
      await api.updatePlaceInList(listId, placeId, updatedPlace);

      // Update the place in the items array for local state
      const currentItems = currentList.items || [];
      const updatedItems = currentItems.map((place) =>
        String(place.id) === String(placeId) ? { ...place, ...updatedPlace } : place
      );

      // Update local state
      const updateListFn = (list) => {
        if (String(list.id) === String(listId)) {
          return {
            ...list,
            items: updatedItems,
          };
        }
        return list;
      };

      if (isCollabList) {
        setCollaborationLists((prevLists) => prevLists.map(updateListFn));
      } else {
        setLists((prevLists) => prevLists.map(updateListFn));
      }
    } catch (error) {
      console.error('Failed to update place in list:', error);
      throw error;
    }
  };

  const deletePlaceFromList = async (listId, placeId) => {
    try {
      // Get the current list (convert to string for consistent comparison)
      // Check both owned lists and collaboration lists
      const currentList = lists.find((list) => String(list.id) === String(listId)) ||
                          collaborationLists.find((list) => String(list.id) === String(listId));
      if (!currentList) {
        throw new Error('List not found');
      }
      const isCollabList = collaborationLists.some((list) => String(list.id) === String(listId));

      // Remove the place from the items array
      const currentItems = currentList.items || [];
      const updatedItems = currentItems.filter((place) => String(place.id) !== String(placeId));

      // Only send items update - backend will recalculate location/category
      // Don't compute locally since cached items may lack addressComponents
      const updates = { items: updatedItems };

      // Update in Firebase
      await api.updateList(listId, updates);

      // Update local state - preserve existing location/category
      // Backend will calculate the correct values
      const updateListFn = (list) => {
        if (String(list.id) === String(listId)) {
          return {
            ...list,
            items: updatedItems,
            // Preserve existing location/category - backend handles the calculation
          };
        }
        return list;
      };

      if (isCollabList) {
        setCollaborationLists((prevLists) => prevLists.map(updateListFn));
      } else {
        setLists((prevLists) => prevLists.map(updateListFn));
      }
    } catch (error) {
      console.error('Failed to delete place from list:', error);
      throw error;
    }
  };

  const createList = async (newList) => {
    try {
      const response = await api.createList(newList);
      const createdList = response.data;
      setLists((prevLists) => {
        const updated = [...prevLists, createdList];
        setCachedLists(LISTS_CACHE_KEY, updated);
        return updated;
      });
      return createdList;
    } catch (error) {
      console.error('Failed to create list:', error);
      throw error;
    }
  };

  const updateList = async (listId, updates) => {
    try {
      await api.updateList(listId, updates);
      setLists((prevLists) => {
        const updated = prevLists.map((list) => (String(list.id) === String(listId) ? { ...list, ...updates } : list));
        setCachedLists(LISTS_CACHE_KEY, updated);
        return updated;
      });
    } catch (error) {
      console.error('Failed to update list:', error);
      throw error;
    }
  };

  // Batch reorder lists - updates state optimistically in one batch, then syncs to API
  // This prevents the "jumping" effect when multiple position updates cause multiple re-renders
  const reorderLists = async (updatesArray) => {
    // updatesArray is an array of { listId, updates } objects
    if (!updatesArray || updatesArray.length === 0) return;

    // 1. Optimistically update local state in one batch
    setLists((prevLists) => {
      const updated = prevLists.map((list) => {
        const update = updatesArray.find(u => String(u.listId) === String(list.id));
        if (update) {
          return { ...list, ...update.updates };
        }
        return list;
      });
      setCachedLists(LISTS_CACHE_KEY, updated);
      return updated;
    });

    // 2. Sync to API in parallel (don't await each one sequentially)
    try {
      await Promise.all(
        updatesArray.map(({ listId, updates }) => api.updateList(listId, updates))
      );
    } catch (error) {
      console.error('Failed to sync reorder to API:', error);
      // On error, refetch to get correct state from server
      const userId = user?.userId || user?.id;
      if (userId) {
        fetchListsInternal(userId);
      }
      throw error;
    }
  };

  const deleteList = async (listId) => {
    try {
      await api.deleteList(listId);
      setLists((prevLists) => {
        const updated = prevLists.filter((list) => String(list.id) !== String(listId));
        setCachedLists(LISTS_CACHE_KEY, updated);
        return updated;
      });
    } catch (error) {
      console.error('Failed to delete list:', error);
      throw error;
    }
  };

  const getListById = useCallback((listId) => {
    // Check both owned lists and collaboration lists
    const ownedList = lists.find((list) => String(list.id) === String(listId));
    if (ownedList) return ownedList;
    return collaborationLists.find((list) => String(list.id) === String(listId));
  }, [lists, collaborationLists]);

  // Update local list state with enriched data (e.g., photoUrls from places collection)
  // This is called when ListDetail fetches enriched data from the API
  const updateListLocally = (listId, enrichedList) => {
    // Update both owned lists and collaboration lists
    setLists((prevLists) =>
      prevLists.map((list) =>
        String(list.id) === String(listId) ? { ...list, ...enrichedList } : list
      )
    );
    setCollaborationLists((prevLists) =>
      prevLists.map((list) =>
        String(list.id) === String(listId) ? { ...list, ...enrichedList } : list
      )
    );
  };

  // Collaborator management
  const addCollaborator = async (listId, collaboratorId) => {
    try {
      await api.addCollaborator(listId, collaboratorId);
      // Refresh the list to get updated collaborators
      const response = await api.getList(listId);
      if (response.data) {
        setLists((prevLists) =>
          prevLists.map((list) =>
            String(list.id) === String(listId) ? { ...list, ...response.data } : list
          )
        );
      }
    } catch (error) {
      console.error('Failed to add collaborator:', error);
      throw error;
    }
  };

  const removeCollaborator = async (listId, collaboratorId) => {
    try {
      await api.removeCollaborator(listId, collaboratorId);
      // Refresh the list to get updated collaborators
      const response = await api.getList(listId);
      if (response.data) {
        setLists((prevLists) =>
          prevLists.map((list) =>
            String(list.id) === String(listId) ? { ...list, ...response.data } : list
          )
        );
      }
    } catch (error) {
      console.error('Failed to remove collaborator:', error);
      throw error;
    }
  };

  const leaveCollaboration = async (listId) => {
    try {
      await api.leaveCollaboration(listId);
      // Remove from collaboration lists
      setCollaborationLists((prevLists) =>
        prevLists.filter((list) => String(list.id) !== String(listId))
      );
    } catch (error) {
      console.error('Failed to leave collaboration:', error);
      throw error;
    }
  };

  const transferOwnership = async (listId, newOwnerId) => {
    try {
      await api.transferOwnership(listId, newOwnerId);
      // The list will now be in collaborationLists, not lists
      // Refresh both
      if (user && (user.userId || user.id)) {
        const userId = user.userId || user.id;
        await fetchLists(userId);
        await fetchCollaborationLists();
      }
    } catch (error) {
      console.error('Failed to transfer ownership:', error);
      throw error;
    }
  };

  // Archive a list for the current user
  const archiveList = async (listId) => {
    try {
      await api.archiveList(listId);

      // Find the list in either lists or collaborationLists
      const ownedList = lists.find((list) => String(list.id) === String(listId));
      const collabList = collaborationLists.find((list) => String(list.id) === String(listId));
      const listToArchive = ownedList || collabList;

      if (listToArchive) {
        // Add to archived lists with isOwned flag
        const archivedEntry = { ...listToArchive, isOwned: !!ownedList };
        setArchivedLists((prevLists) => {
          const updated = [archivedEntry, ...prevLists];
          setCachedLists(ARCHIVED_CACHE_KEY, updated);
          return updated;
        });
      }

      // Remove from active lists and update caches
      if (ownedList) {
        setLists((prevLists) => {
          const updated = prevLists.filter((list) => String(list.id) !== String(listId));
          setCachedLists(LISTS_CACHE_KEY, updated);
          return updated;
        });
      }
      if (collabList) {
        setCollaborationLists((prevLists) => {
          const updated = prevLists.filter((list) => String(list.id) !== String(listId));
          setCachedLists(COLLAB_CACHE_KEY, updated);
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to archive list:', error);
      throw error;
    }
  };

  // Unarchive (restore) a list for the current user
  const unarchiveList = async (listId) => {
    try {
      await api.unarchiveList(listId);

      // Find the list in archived lists
      const archivedList = archivedLists.find((list) => String(list.id) === String(listId));

      if (archivedList) {
        // Remove from archived lists and update cache
        setArchivedLists((prevLists) => {
          const updated = prevLists.filter((list) => String(list.id) !== String(listId));
          setCachedLists(ARCHIVED_CACHE_KEY, updated);
          return updated;
        });

        // Add back to the appropriate list (owned or collaboration)
        // Check isOwned explicitly, or fall back to comparing userId with current user
        const currentUserId = user?.userId || user?.id;
        const isOwnedList = archivedList.isOwned === true ||
          (archivedList.isOwned === undefined && archivedList.userId === currentUserId);

        if (isOwnedList) {
          setLists((prevLists) => {
            const updated = [...prevLists, archivedList];
            setCachedLists(LISTS_CACHE_KEY, updated);
            return updated;
          });
        } else {
          setCollaborationLists((prevLists) => {
            const updated = [...prevLists, archivedList];
            setCachedLists(COLLAB_CACHE_KEY, updated);
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Failed to unarchive list:', error);
      throw error;
    }
  };

  const value = {
    lists,
    collaborationLists,
    archivedLists,
    archivedLoaded,
    loadingArchived,
    loading,
    fetchLists,
    fetchCollaborationLists,
    fetchArchivedLists,
    addPlaceToList,
    addPlaceToMultipleLists,
    deletePlaceFromMultipleLists,
    updatePlaceInList,
    deletePlaceFromList,
    createList,
    updateList,
    reorderLists,
    updateListLocally,
    deleteList,
    getListById,
    // Collaborator management
    addCollaborator,
    removeCollaborator,
    leaveCollaboration,
    transferOwnership,
    // Archive management
    archiveList,
    unarchiveList,
  };

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}
